import { Link } from 'react-router-dom';
import { 
  ArrowUpRight, Code, Cloud, Rocket, Database, 
  Layout, Terminal, CheckCircle, Clock, Users, Award, Sparkles, Brain
} from 'lucide-react';

const Programs = () => {
  const programs = [
    {
      id: 'ai-prompt-engineering',
      title: 'AI Prompt Engineering Bootcamp',
      subtitle: 'NEW — Master AI',
      duration: '4 hours',
      icon: Brain,
      color: '#A855F7',
      description: 'Learn to communicate effectively with AI. Master prompt engineering techniques to unlock the full potential of ChatGPT and other AI tools.',
      skills: ['P.R.O.M.P.T. Framework', 'Few-Shot Learning', 'Chain-of-Thought Prompting', 'AI Personas & Formatting', 'Prompt Chaining'],
      outcomes: ['Write effective AI prompts', 'Use AI for productivity', 'Build AI-powered workflows', 'Earn AI Master certification'],
      featured: true,
      detailsLink: '/programs/ai'
    },
    {
      id: 'web-foundations',
      title: 'Web Development Foundations',
      subtitle: 'Start Your Journey',
      duration: '8 weeks',
      icon: Layout,
      color: '#00AEEF',
      description: 'Learn the fundamentals of web development from scratch. No prior experience needed.',
      skills: ['HTML5 & CSS3', 'JavaScript Fundamentals', 'Responsive Design', 'Git Version Control'],
      outcomes: ['Build responsive websites', 'Understand coding logic', 'Create a personal portfolio']
    },
    {
      id: 'app-development',
      title: 'App Development Track',
      subtitle: 'Level Up Your Skills',
      duration: '8 weeks',
      icon: Code,
      color: '#00FFE0',
      description: 'From beginner concepts to deployed applications. Learn to design, build, and deploy full-stack web applications using industry-standard tools and frameworks.',
      skills: ['React.js Framework', 'Frontend Development', 'Backend Development', 'Database Integration', 'API Development', 'Git & Version Control', 'Cloud Deployment'],
      outcomes: ['Build full-stack web applications', 'Connect frontend and backend systems', 'Work with APIs and databases', 'Deploy applications to production', 'Present projects during Demo Day']
    },
    {
      id: 'cloud-devops',
      title: 'Cloud & DevOps Intro',
      subtitle: 'Enterprise Skills',
      duration: '6 weeks',
      icon: Cloud,
      color: '#FF6A00',
      description: 'Learn how modern companies build, deploy, and manage applications at scale. Gain hands-on exposure to cloud platforms, automation workflows, containers, and deployment pipelines.',
      skills: ['Cloud Platform Fundamentals', 'CI/CD Pipelines', 'Container Basics (Docker)', 'Infrastructure Setup', 'DevOps Workflow Fundamentals', 'Deployment Automation', 'Monitoring & Production Concepts'],
      outcomes: ['Deploy applications to the cloud', 'Build basic CI/CD pipelines', 'Work with containers and infrastructure', 'Understand enterprise DevOps practices', 'Gain exposure to cloud engineering careers']
    }
  ];

  const schedule = [
    { week: '1-2', title: 'Foundations', desc: 'Core concepts, environment setup, first lines of code' },
    { week: '3-4', title: 'Building', desc: 'Project development, hands-on coding, real-world tools' },
    { week: '5-6', title: 'Integration', desc: 'Connecting pieces, databases, APIs, deployment basics' },
    { week: '7-8', title: 'Polish & Present', desc: 'Final touches, demo prep, presentation skills' },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32 bg-[#060F22] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#0A1F44] via-[#060F22] to-[#060F22]"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-6 block">
              Our Programs
            </span>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white leading-tight mb-6">
              From Zero Experience to{' '}
              <span className="gradient-text">Deployed Applications</span>
            </h1>
            <p className="text-lg sm:text-xl text-[#94A3B8] leading-relaxed">
              Our hands-on programs are designed to take you from complete beginner to confident builder in just 8 weeks. Real projects. Real skills. Real results.
            </p>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Choose Your Path
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Program Tracks
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Each track is designed to build practical skills through project-based learning.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {programs.map((program) => (
              <div
                key={program.id}
                className={`bg-[#0A1F44]/50 backdrop-blur-md border rounded-2xl overflow-hidden hover:border-[#00FFE0]/50 transition-all duration-500 flex flex-col ${program.featured ? 'border-[#A855F7]/50 ring-2 ring-[#A855F7]/20' : 'border-[#00AEEF]/20'}`}
              >
                {program.featured && (
                  <div className="bg-gradient-to-r from-[#A855F7] to-[#EC4899] px-4 py-2 flex items-center justify-center gap-2">
                    <Sparkles className="w-4 h-4 text-white" />
                    <span className="text-white text-sm font-bold uppercase tracking-wider">New Course Available</span>
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="p-8 flex-grow">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                    style={{ backgroundColor: `${program.color}20` }}
                  >
                    <program.icon className="w-8 h-8" style={{ color: program.color }} />
                  </div>
                  
                  <span 
                    className="text-xs font-bold uppercase tracking-[0.2em] mb-2 block"
                    style={{ color: program.color }}
                  >
                    {program.subtitle}
                  </span>
                  
                  <h3 className="text-2xl font-bold text-white mb-3">{program.title}</h3>
                  
                  <div className="flex items-center gap-2 mb-4 text-[#94A3B8]">
                    <Clock className="w-4 h-4" />
                    <span>{program.duration}</span>
                  </div>
                  
                  <p className="text-[#94A3B8] leading-relaxed mb-6">{program.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-white mb-3">Skills You&apos;ll Learn:</h4>
                    <ul className="space-y-2">
                      {program.skills.map((skill, i) => (
                        <li key={`skill-${program.id}-${i}`} className="flex items-center gap-2 text-[#94A3B8] text-sm">
                          <CheckCircle className="w-4 h-4 text-[#00FFE0] flex-shrink-0" />
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-3">What You&apos;ll Achieve:</h4>
                    <ul className="space-y-2">
                      {program.outcomes.map((outcome, i) => (
                        <li key={`outcome-${program.id}-${i}`} className="flex items-center gap-2 text-[#94A3B8] text-sm">
                          <Award className="w-4 h-4 text-[#FF6A00] flex-shrink-0" />
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div className="p-8 pt-0 flex gap-3">
                  {program.detailsLink && (
                    <Link
                      to={program.detailsLink}
                      data-testid={`program-${program.id}-details-btn`}
                      className="inline-flex items-center justify-center flex-1 bg-transparent border-2 border-[#A855F7] text-[#A855F7] font-bold rounded-xl px-6 py-3 hover:bg-[#A855F7]/10 transition-all duration-300 gap-2"
                    >
                      Learn More
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  )}
                  <Link
                    to="/enrollment"
                    data-testid={`program-${program.id}-enroll-btn`}
                    className={`inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-xl px-6 py-3 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2 ${program.detailsLink ? 'flex-1' : 'w-full'}`}
                  >
                    Enroll Now
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section className="py-24 sm:py-32 bg-[#060F22]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Program Structure
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              8 Weeks That Change Everything
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Our curriculum is designed for maximum impact—intensive, focused, and results-driven.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {schedule.map((phase, index) => (
              <div key={`phase-${phase.week}-${phase.title}`} className="relative">
                {index < schedule.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 right-0 w-full h-0.5 bg-gradient-to-r from-[#00AEEF] to-[#00FFE0]" style={{ transform: 'translateX(50%)' }}></div>
                )}
                <div className="bg-[#0A1F44]/50 backdrop-blur-md border border-[#00AEEF]/20 rounded-2xl p-6 relative z-10">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00AEEF] to-[#00FFE0] flex items-center justify-center mb-4">
                    <span className="font-bold text-[#060F22]">{index + 1}</span>
                  </div>
                  <span className="text-[#00FFE0] font-semibold text-sm">Week {phase.week}</span>
                  <h3 className="text-xl font-bold text-white mt-1 mb-2">{phase.title}</h3>
                  <p className="text-[#94A3B8] text-sm">{phase.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud & DevOps Detailed Curriculum */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/50" id="cloud-devops-curriculum">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="w-16 h-16 rounded-2xl bg-[#FF6A00]/20 flex items-center justify-center mx-auto mb-6">
              <Cloud className="w-8 h-8 text-[#FF6A00]" />
            </div>
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#FF6A00] mb-4 block">
              Enterprise Skills Track
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              Cloud & DevOps Intro Curriculum
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Learn how modern companies build, deploy, and manage applications at scale.
            </p>
          </div>

          {/* 6-Week Curriculum Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {/* Week 1 */}
            <div className="bg-[#060F22]/80 border border-[#FF6A00]/20 rounded-2xl p-6 hover:border-[#FF6A00]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FF6A00] flex items-center justify-center">
                  <span className="font-bold text-white text-sm">1</span>
                </div>
                <h3 className="text-lg font-bold text-white">Introduction to Cloud Computing</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />What is cloud computing?</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Understanding AWS, Azure, and Google Cloud</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Cloud vs traditional infrastructure</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Enterprise technology overview</li>
              </ul>
              <div className="bg-[#FF6A00]/10 rounded-lg p-3">
                <p className="text-[#FF6A00] text-xs font-semibold">Deliverable: Cloud platform setup and orientation</p>
              </div>
            </div>

            {/* Week 2 */}
            <div className="bg-[#060F22]/80 border border-[#FF6A00]/20 rounded-2xl p-6 hover:border-[#FF6A00]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FF6A00] flex items-center justify-center">
                  <span className="font-bold text-white text-sm">2</span>
                </div>
                <h3 className="text-lg font-bold text-white">Infrastructure & Virtual Environments</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Servers and virtual machines</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Networking fundamentals</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Storage and databases</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Infrastructure basics</li>
              </ul>
              <div className="bg-[#FF6A00]/10 rounded-lg p-3">
                <p className="text-[#FF6A00] text-xs font-semibold">Deliverable: Functional cloud server environment</p>
              </div>
            </div>

            {/* Week 3 */}
            <div className="bg-[#060F22]/80 border border-[#FF6A00]/20 rounded-2xl p-6 hover:border-[#FF6A00]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FF6A00] flex items-center justify-center">
                  <span className="font-bold text-white text-sm">3</span>
                </div>
                <h3 className="text-lg font-bold text-white">DevOps Fundamentals & CI/CD</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />What is DevOps?</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Understanding CI/CD pipelines</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Source control with Git & GitHub</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Automation basics</li>
              </ul>
              <div className="bg-[#FF6A00]/10 rounded-lg p-3">
                <p className="text-[#FF6A00] text-xs font-semibold">Deliverable: Beginner CI/CD pipeline</p>
              </div>
            </div>

            {/* Week 4 */}
            <div className="bg-[#060F22]/80 border border-[#FF6A00]/20 rounded-2xl p-6 hover:border-[#FF6A00]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FF6A00] flex items-center justify-center">
                  <span className="font-bold text-white text-sm">4</span>
                </div>
                <h3 className="text-lg font-bold text-white">Containers & Deployment</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Introduction to Docker</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Container basics</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Application packaging</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Deployment concepts</li>
              </ul>
              <div className="bg-[#FF6A00]/10 rounded-lg p-3">
                <p className="text-[#FF6A00] text-xs font-semibold">Deliverable: Containerized application deployment</p>
              </div>
            </div>

            {/* Week 5 */}
            <div className="bg-[#060F22]/80 border border-[#FF6A00]/20 rounded-2xl p-6 hover:border-[#FF6A00]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FF6A00] flex items-center justify-center">
                  <span className="font-bold text-white text-sm">5</span>
                </div>
                <h3 className="text-lg font-bold text-white">Monitoring, Security & Practices</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Monitoring systems</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Logging and troubleshooting</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Cloud security basics</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Enterprise collaboration workflows</li>
              </ul>
              <div className="bg-[#FF6A00]/10 rounded-lg p-3">
                <p className="text-[#FF6A00] text-xs font-semibold">Deliverable: Monitoring and operations exercise</p>
              </div>
            </div>

            {/* Week 6 */}
            <div className="bg-[#060F22]/80 border border-[#FF6A00]/20 rounded-2xl p-6 hover:border-[#FF6A00]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#FF6A00] flex items-center justify-center">
                  <span className="font-bold text-white text-sm">6</span>
                </div>
                <h3 className="text-lg font-bold text-white">Demo Day & Career Readiness</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Enterprise cloud career paths</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Professional presentation skills</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Real-world deployment practices</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Team collaboration and communication</li>
              </ul>
              <div className="bg-[#FF6A00]/10 rounded-lg p-3">
                <p className="text-[#FF6A00] text-xs font-semibold">Deliverable: Final cloud deployment presentation</p>
              </div>
            </div>
          </div>

          {/* Career Paths */}
          <div className="bg-[#060F22]/80 border border-[#00AEEF]/20 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Career Paths This Program Prepares You For</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {['Cloud Engineer', 'DevOps Engineer', 'Site Reliability Engineer', 'Platform Engineer', 'Infrastructure Engineer', 'Systems Administrator'].map((career) => (
                <div key={`cloud-career-${career}`} className="bg-[#0A1F44]/50 rounded-xl p-4 text-center">
                  <Terminal className="w-6 h-6 text-[#00AEEF] mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">{career}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* App Development Track Detailed Curriculum */}
      <section className="py-24 sm:py-32 bg-[#060F22]" id="app-development-curriculum">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="w-16 h-16 rounded-2xl bg-[#00FFE0]/20 flex items-center justify-center mx-auto mb-6">
              <Code className="w-8 h-8 text-[#00FFE0]" />
            </div>
            <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
              Level Up Your Skills
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
              App Development Track Curriculum
            </h2>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              From beginner concepts to deployed applications. Build full-stack web applications using industry-standard tools.
            </p>
          </div>

          {/* 8-Week Curriculum Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* Week 1 */}
            <div className="bg-[#0A1F44]/80 border border-[#00FFE0]/20 rounded-2xl p-6 hover:border-[#00FFE0]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#00FFE0] flex items-center justify-center">
                  <span className="font-bold text-[#060F22] text-sm">1</span>
                </div>
                <h3 className="text-base font-bold text-white">Web Development Foundations</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Internet & website fundamentals</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Intro to WordPress & Drupal</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Frontend vs backend</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Setting up dev tools</li>
              </ul>
              <div className="bg-[#00FFE0]/10 rounded-lg p-3">
                <p className="text-[#00FFE0] text-xs font-semibold">Deliverable: Personal profile webpage</p>
              </div>
            </div>

            {/* Week 2 */}
            <div className="bg-[#0A1F44]/80 border border-[#00FFE0]/20 rounded-2xl p-6 hover:border-[#00FFE0]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#00FFE0] flex items-center justify-center">
                  <span className="font-bold text-[#060F22] text-sm">2</span>
                </div>
                <h3 className="text-base font-bold text-white">Tech Careers & Industry</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Software development careers</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Salaries & job roles</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Web developer pathways</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Corporate tech environments</li>
              </ul>
              <div className="bg-[#00FFE0]/10 rounded-lg p-3">
                <p className="text-[#00FFE0] text-xs font-semibold">Deliverable: Career exploration presentation</p>
              </div>
            </div>

            {/* Week 3 */}
            <div className="bg-[#0A1F44]/80 border border-[#00FFE0]/20 rounded-2xl p-6 hover:border-[#00FFE0]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#00FFE0] flex items-center justify-center">
                  <span className="font-bold text-[#060F22] text-sm">3</span>
                </div>
                <h3 className="text-base font-bold text-white">HTML, CSS & Responsive Design</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />HTML page structure</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />CSS styling fundamentals</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Responsive web design</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />UI/UX basics</li>
              </ul>
              <div className="bg-[#00FFE0]/10 rounded-lg p-3">
                <p className="text-[#00FFE0] text-xs font-semibold">Deliverable: Responsive portfolio website</p>
              </div>
            </div>

            {/* Week 4 */}
            <div className="bg-[#0A1F44]/80 border border-[#00FFE0]/20 rounded-2xl p-6 hover:border-[#00FFE0]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#00FFE0] flex items-center justify-center">
                  <span className="font-bold text-[#060F22] text-sm">4</span>
                </div>
                <h3 className="text-base font-bold text-white">JavaScript Fundamentals</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Variables & data types</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Functions & loops</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />DOM manipulation</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Events & interactivity</li>
              </ul>
              <div className="bg-[#00FFE0]/10 rounded-lg p-3">
                <p className="text-[#00FFE0] text-xs font-semibold">Deliverable: Interactive JavaScript project</p>
              </div>
            </div>

            {/* Week 5 */}
            <div className="bg-[#0A1F44]/80 border border-[#00FFE0]/20 rounded-2xl p-6 hover:border-[#00FFE0]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#00FFE0] flex items-center justify-center">
                  <span className="font-bold text-[#060F22] text-sm">5</span>
                </div>
                <h3 className="text-base font-bold text-white">React.js Frontend Development</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />React components</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Props & state management</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Routing</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Frontend app structure</li>
              </ul>
              <div className="bg-[#00FFE0]/10 rounded-lg p-3">
                <p className="text-[#00FFE0] text-xs font-semibold">Deliverable: React-based web application</p>
              </div>
            </div>

            {/* Week 6 */}
            <div className="bg-[#0A1F44]/80 border border-[#00FFE0]/20 rounded-2xl p-6 hover:border-[#00FFE0]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#00FFE0] flex items-center justify-center">
                  <span className="font-bold text-[#060F22] text-sm">6</span>
                </div>
                <h3 className="text-base font-bold text-white">Backend Development & APIs</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Backend fundamentals</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />APIs & JSON</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Server-side logic</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Database concepts</li>
              </ul>
              <div className="bg-[#00FFE0]/10 rounded-lg p-3">
                <p className="text-[#00FFE0] text-xs font-semibold">Deliverable: API-connected application</p>
              </div>
            </div>

            {/* Week 7 */}
            <div className="bg-[#0A1F44]/80 border border-[#00FFE0]/20 rounded-2xl p-6 hover:border-[#00FFE0]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#00FFE0] flex items-center justify-center">
                  <span className="font-bold text-[#060F22] text-sm">7</span>
                </div>
                <h3 className="text-base font-bold text-white">Full-Stack Integration</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Database integration</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Authentication basics</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Production deployment</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Debugging & testing</li>
              </ul>
              <div className="bg-[#00FFE0]/10 rounded-lg p-3">
                <p className="text-[#00FFE0] text-xs font-semibold">Deliverable: Live deployed application</p>
              </div>
            </div>

            {/* Week 8 */}
            <div className="bg-[#0A1F44]/80 border border-[#00FFE0]/20 rounded-2xl p-6 hover:border-[#00FFE0]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#00FFE0] flex items-center justify-center">
                  <span className="font-bold text-[#060F22] text-sm">8</span>
                </div>
                <h3 className="text-base font-bold text-white">Demo Day & Career Readiness</h3>
              </div>
              <ul className="space-y-2 text-[#94A3B8] text-sm mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Professional presentations</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Project pitching</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Resume & portfolio</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#00FFE0] mt-0.5 flex-shrink-0" />Industry feedback</li>
              </ul>
              <div className="bg-[#00FFE0]/10 rounded-lg p-3">
                <p className="text-[#00FFE0] text-xs font-semibold">Deliverable: Final Demo Day Presentation</p>
              </div>
            </div>
          </div>

          {/* What Students Leave With */}
          <div className="bg-[#0A1F44]/80 border border-[#00AEEF]/20 rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">What Students Leave With</h3>
            <div className="grid md:grid-cols-5 gap-4">
              {[
                'Portfolio-ready project',
                'Real-world coding exposure',
                'Increased career confidence',
                'Hands-on development experience',
                'Industry expectations knowledge'
              ].map((item) => (
                <div key={`outcome-${item}`} className="bg-[#060F22]/50 rounded-xl p-4 text-center">
                  <Award className="w-6 h-6 text-[#00FFE0] mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Career Paths */}
          <div className="bg-[#060F22]/80 border border-[#00AEEF]/20 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Career Paths This Program Prepares You For</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {['Frontend Developer', 'Backend Developer', 'Full-Stack Developer', 'Web Developer', 'Software Engineer', 'UI/UX Developer'].map((career) => (
                <div key={`app-career-${career}`} className="bg-[#0A1F44]/50 rounded-xl p-4 text-center">
                  <Code className="w-6 h-6 text-[#00FFE0] mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">{career}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 sm:py-32 bg-[#0A1F44]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00FFE0] mb-4 block">
                What&apos;s Included
              </span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
                Everything You Need to Succeed
              </h2>
              <ul className="space-y-4">
                {[
                  'Live instruction sessions with industry professionals',
                  'Hands-on projects with real-world applications',
                  'One-on-one mentorship and code reviews',
                  'Access to learning platform and resources',
                  'Demo Day presentation to tech professionals',
                  'Certificate of completion',
                  'Career guidance and job placement support',
                  'Alumni network and community access'
                ].map((item) => (
                  <li key={`included-${item.slice(0, 20)}`} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#00FFE0] flex-shrink-0 mt-0.5" />
                    <span className="text-[#94A3B8] text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF6A00]/20 to-[#00AEEF]/20 rounded-3xl blur-3xl"></div>
              <img
                src="https://images.pexels.com/photos/1708912/pexels-photo-1708912.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Demo Day Presentation"
                className="relative rounded-3xl border border-[#00AEEF]/20 w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#060F22] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#00AEEF]/10 via-transparent to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-[#94A3B8] leading-relaxed mb-8 max-w-2xl mx-auto">
            Applications are now open for our next cohort. No experience required—just the drive to learn and build.
          </p>
          <Link
            to="/enrollment"
            data-testid="programs-cta-enroll-btn"
            className="inline-flex items-center justify-center bg-[#FF6A00] text-white font-bold rounded-full px-8 py-4 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] hover:-translate-y-1 transition-all duration-300 gap-2 text-lg"
          >
            Apply Now
            <ArrowUpRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Programs;
